import React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Save,
  Trash2,
  Search,
  Plus,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  CheckCircle2,
  Download,
  Upload,
  FileText,
  Lock,
} from '../icons';
import { cx } from '../ui';
import {
  PGA_FLAG_SCHEMAS,
  computeCompleteness,
  normalizePgaFlagPayload,
  pgaBadgeTone,
  resolvePgaFlagSchema,
  type PgaCode,
  type PgaField,
  type PgaSubGroup,
} from '../lib/pga-schema';
import type { AppliedPgaFlag, PgaFlagDocument } from '../lib/pga-applied';
import type { ParsedTemplate } from '../lib/pga-template';
import {
  downloadImportPgaFlagTemplate,
  fetchImportPgaFlagDefinition,
  previewImportPgaFlagTemplate,
  type ImportPgaFlagDefinition,
} from '../lib/import-service';
import { useImportHubReadOnly } from '../lib/access';
import { getLinkedPgaHitSnapshot } from '../lib/pga-hit-prefill';

const TONE_CLS: Record<'info' | 'warning' | 'danger', string> = {
  info: 'ih-badge-info',
  warning: 'ih-badge-warning',
  danger: 'ih-badge-danger',
};

export const PgaFlagEditorDrawer: React.FC<{
  open: boolean;
  code: PgaCode | null;
  flag: AppliedPgaFlag | null;
  onClose: () => void;
  onSave: (
    values: Record<string, unknown>,
    repeaters: Record<string, Record<string, unknown>[]>,
  ) => Promise<boolean | void> | boolean | void;
  onRemove: () => void;
  isDraft?: boolean;
  onAddDocument?: (doc: { name: string; kind: string; size: number }) => void;
  onRemoveDocument?: (docId: string) => void;
}> = ({
  open,
  code,
  flag,
  onClose,
  onSave,
  onRemove,
  isDraft = false,
  onAddDocument,
  onRemoveDocument,
}) => {
  const readOnly = useImportHubReadOnly();
  const [definition, setDefinition] = useState<ImportPgaFlagDefinition | null>(
    null,
  );
  const schema = useMemo(
    () => (code ? resolvePgaFlagSchema(code, definition) : null),
    [code, definition],
  );

  const [values, setValues] = useState<Record<string, unknown>>({});
  const [repeaters, setRepeaters] = useState<
    Record<string, Record<string, unknown>[]>
  >({});

  const [dirty, setDirty] = useState(false);
  const [search, setSearch] = useState('');
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [reviewParsed, setReviewParsed] = useState<ParsedTemplate | null>(null);
  const [reviewFileName, setReviewFileName] = useState<string | null>(null);
  const [templateBusy, setTemplateBusy] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const templateInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);
  // Tracks user edits (as opposed to `dirty`, which starts true for drafts) so
  // an asynchronously resolved schema never overwrites in-flight changes.
  const editedRef = useRef(false);
  const initializedRef = useRef<{
    flag: AppliedPgaFlag | null;
    code: PgaCode | null;
  }>({ flag: null, code: null });

  useEffect(() => {
    if (!open || typeof window === 'undefined') return;
    const originalStyle = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !code) return;
    let cancelled = false;
    setDefinition(null);
    fetchImportPgaFlagDefinition(code).then((result) => {
      if (cancelled) return;
      if (result.success && result.definition) {
        setDefinition(result.definition);
        setTemplateError(null);
      } else {
        setTemplateError(result.error ?? 'Failed to load PGA fields.');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [code, open]);

  useEffect(() => {
    if (!open || !flag || !code) {
      if (!open) initializedRef.current = { flag: null, code: null };
      return;
    }
    const isNewTarget =
      initializedRef.current.flag !== flag ||
      initializedRef.current.code !== code;
    // The workbook definition loads asynchronously, so `schema` can change
    // after the user has started typing. Only re-normalize for the new schema
    // while the form is still pristine.
    if (!isNewTarget && editedRef.current) return;
    initializedRef.current = { flag, code };
    editedRef.current = false;
    const normalized = normalizePgaFlagPayload(
      code,
      flag.values ?? {},
      flag.repeaters ?? {},
      schema ?? PGA_FLAG_SCHEMAS[code],
    );
    setValues(normalized.values);
    setRepeaters(normalized.repeaters);
    setDirty(isDraft);
    setSearch('');
    setConfirmRemove(false);
    setSavedFlash(false);
    const c: Record<string, boolean> = {};
    schema?.subGroups.forEach((sg) => {
      c[sg.key] = !(sg.defaultOpen || sg.fields.some((f) => f.required));
    });
    setCollapsed(c);
    setTemplateError(null);
    setSaving(false);
  }, [open, flag, code, isDraft, schema]);

  useEffect(() => {
    if (!open || !schema) return;
    setCollapsed((current) => {
      const next = { ...current };
      schema.subGroups.forEach((sg) => {
        if (next[sg.key] === undefined) {
          next[sg.key] = !(sg.defaultOpen || sg.fields.some((f) => f.required));
        }
      });
      return next;
    });
  }, [open, schema]);

  const completeness = useMemo(
    () =>
      schema
        ? computeCompleteness(schema, values, repeaters)
        : { filled: 0, total: 0, requiredMissing: 0 },
    [schema, values, repeaters],
  );
  const linkedHitSnapshot = useMemo(
    () => getLinkedPgaHitSnapshot(values),
    [values],
  );

  if (!open || !schema || !code || !flag) return null;
  if (typeof document === 'undefined') return null;

  const markEdited = () => {
    editedRef.current = true;
    setDirty(true);
  };
  const setField = (subKey: string, field: PgaField, v: unknown) => {
    const storageKey = field.storageKey ?? `${subKey}.${field.key}`;
    setValues((prev) => ({ ...prev, [storageKey]: v }));
    markEdited();
  };
  const setRepeaterField = (
    sgKey: string,
    idx: number,
    field: PgaField,
    v: unknown,
  ) => {
    setRepeaters((prev) => {
      const list = [...(prev[sgKey] ?? [])];
      list[idx] = { ...list[idx], [field.key]: v };
      return { ...prev, [sgKey]: list };
    });
    const workbookStorageKey = field.workbookStorageKeys?.[idx];
    if (workbookStorageKey) {
      setValues((prev) => ({ ...prev, [workbookStorageKey]: v }));
    }
    markEdited();
  };
  const addRepeater = (sg: PgaSubGroup) => {
    setRepeaters((prev) => {
      const list = [...(prev[sg.key] ?? [])];
      if (sg.maxInstances && list.length >= sg.maxInstances) return prev;
      list.push({});
      return { ...prev, [sg.key]: list };
    });
    markEdited();
  };
  const removeRepeater = (sg: PgaSubGroup, idx: number) => {
    const list = [...(repeaters[sg.key] ?? [])];
    list.splice(idx, 1);
    // Rebuild every workbook-backed value from the spliced list so later rows
    // shift down with it — deleting only this row's keys would leave the
    // surviving keys at their original index and re-project a ghost row on the
    // next normalization.
    const hasWorkbookKeys = sg.fields.some(
      (field) => field.workbookStorageKeys?.length,
    );
    if (hasWorkbookKeys) {
      setValues((prev) => {
        const next = { ...prev };
        sg.fields.forEach((field) => {
          const storageKeys = field.workbookStorageKeys;
          if (!storageKeys?.length) return;
          storageKeys.forEach((storageKey, index) => {
            if (!storageKey) return;
            const value = list[index]?.[field.key];
            if (value === undefined || value === null || value === '') {
              delete next[storageKey];
            } else {
              next[storageKey] = value;
            }
          });
        });
        return next;
      });
    }
    setRepeaters((prev) => ({ ...prev, [sg.key]: list }));
    markEdited();
  };

  const handleSave = async () => {
    if (completeness.requiredMissing > 0) return;
    setSaving(true);
    const saved = await onSave(values, repeaters);
    setSaving(false);
    if (saved === false) return;
    setDirty(false);
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1600);
  };

  const handleTemplateFile = async (file: File) => {
    if (!code) return;
    setTemplateBusy(true);
    setTemplateError(null);
    try {
      const text = await file.text();
      const result = await previewImportPgaFlagTemplate(code, text);
      if (!result.success || !result.preview) {
        setReviewParsed(null);
        setTemplateError(result.error ?? 'Failed to read the PGA template.');
        return;
      }
      setReviewParsed(result.preview);
      setReviewFileName(file.name);
    } finally {
      setTemplateBusy(false);
    }
  };

  const handleTemplateDownload = async () => {
    if (!code) return;
    setTemplateBusy(true);
    setTemplateError(null);
    const result = await downloadImportPgaFlagTemplate(code);
    if (!result.success) {
      setTemplateError(result.error ?? 'Failed to download the PGA template.');
    }
    setTemplateBusy(false);
  };

  const applyParsedValues = () => {
    if (!reviewParsed) return;
    setValues((prev) => ({ ...prev, ...reviewParsed.values }));
    setRepeaters((prev) => ({ ...prev, ...reviewParsed.repeaters }));
    markEdited();
    setReviewParsed(null);
    setReviewFileName(null);
  };

  const handleDocFile = (file: File) => {
    if (!onAddDocument) return;
    onAddDocument({
      name: file.name,
      kind: file.type || 'application/octet-stream',
      size: file.size,
    });
  };

  const needle = search.trim().toLowerCase();
  const matchesSearch = (f: PgaField) =>
    !needle ||
    f.label.toLowerCase().includes(needle) ||
    f.key.toLowerCase().includes(needle);

  const tone = pgaBadgeTone(code);
  const pct = completeness.total
    ? Math.round((completeness.filled / completeness.total) * 100)
    : 0;
  const fillTone =
    completeness.requiredMissing === 0 && completeness.filled > 0
      ? 'success'
      : completeness.requiredMissing > 0
      ? 'warning'
      : 'info';

  return createPortal(
    <div className='ih-pga-drawer-shell'>
      <div
        className='ih-modal-backdrop'
        style={{ position: 'absolute', padding: 0 }}
        onClick={onClose}
        role='presentation'
      />
      <aside
        className='ih-pga-drawer import-hub'
        role='dialog'
        aria-modal='true'
        style={{ zIndex: 51, maxHeight: '100vh' }}
      >
        <header className='ih-pga-drawer-header'>
          <div
            className='ih-row ih-between ih-gap-3'
            style={{ alignItems: 'flex-start' }}
          >
            <div className='ih-row ih-gap-2' style={{ minWidth: 0 }}>
              <span className={cx('ih-badge', TONE_CLS[tone])}>
                {code.replace('_', ' ')}
              </span>
              <div style={{ minWidth: 0 }}>
                <div className='ih-kicker'>PGA flag editor</div>
                <h3
                  className='ih-font-semibold ih-truncate'
                  style={{ fontSize: 13 }}
                >
                  {schema.displayName}
                </h3>
              </div>
            </div>
            <button
              type='button'
              onClick={onClose}
              className='ih-icon-btn'
              aria-label='Close'
            >
              <X size={16} />
            </button>
          </div>
          <div className='ih-row ih-gap-3 ih-mt-3'>
            <div className='ih-pga-chip-bar-track' style={{ height: 6 }}>
              <div
                className={cx('ih-pga-chip-bar-fill', fillTone)}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span
              className='ih-text-xs ih-text-muted ih-num'
              style={{ flexShrink: 0 }}
            >
              {completeness.filled} / {completeness.total} filled
            </span>
            {completeness.requiredMissing > 0 && (
              <span className='ih-row ih-gap-1 ih-tone-warning ih-text-xs ih-font-semibold'>
                <AlertCircle size={12} /> {completeness.requiredMissing}{' '}
                required
              </span>
            )}
          </div>
          <div className='ih-row ih-gap-2 ih-mt-3'>
            <button
              type='button'
              className='ih-btn ih-btn-primary ih-btn-sm'
              onClick={handleSave}
              disabled={
                !dirty || completeness.requiredMissing > 0 || saving || readOnly
              }
              title={readOnly ? 'Requires a Business license' : undefined}
            >
              {readOnly ? <Lock size={14} /> : <Save size={14} />}{' '}
              {saving ? 'Saving…' : 'Save'} {dirty && !saving && '•'}
            </button>
            <button
              type='button'
              className='ih-btn ih-btn-outline ih-btn-sm'
              onClick={() => setConfirmRemove(true)}
              disabled={readOnly}
              title={readOnly ? 'Requires a Business license' : undefined}
            >
              {readOnly ? <Lock size={14} /> : <Trash2 size={14} />}{' '}
              {isDraft ? 'Discard' : 'Remove'}
            </button>
            {savedFlash && (
              <span className='ih-row ih-gap-1 ih-tone-success ih-text-xs ih-font-medium'>
                <CheckCircle2 size={12} /> Saved
              </span>
            )}
            {dirty && !savedFlash && (
              <span className='ih-text-xs ih-text-muted ih-italic'>
                Unsaved changes
              </span>
            )}
          </div>
          <div className='ih-row ih-gap-2 ih-mt-3 ih-row-wrap'>
            <button
              type='button'
              className='ih-btn ih-btn-outline ih-btn-sm'
              onClick={handleTemplateDownload}
              disabled={templateBusy}
            >
              <Download size={14} /> Download template
            </button>
            <button
              type='button'
              className='ih-btn ih-btn-outline ih-btn-sm'
              onClick={() => templateInputRef.current?.click()}
              disabled={templateBusy || readOnly}
              title={readOnly ? 'Requires a Business license' : undefined}
            >
              {readOnly ? <Lock size={14} /> : <Upload size={14} />} Upload
              template
            </button>
            <button
              type='button'
              className='ih-btn ih-btn-outline ih-btn-sm'
              onClick={() => docInputRef.current?.click()}
              disabled={!onAddDocument || readOnly}
              title={readOnly ? 'Requires a Business license' : undefined}
            >
              {readOnly ? <Lock size={14} /> : <FileText size={14} />} Attach
              document
            </button>
            <input
              ref={templateInputRef}
              type='file'
              accept='.csv,text/csv'
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleTemplateFile(f);
                e.target.value = '';
              }}
            />
            <input
              ref={docInputRef}
              type='file'
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleDocFile(f);
                e.target.value = '';
              }}
            />
          </div>
          {templateError && (
            <div className='ih-text-xs ih-tone-danger ih-mt-2'>
              {templateError}
            </div>
          )}

          {linkedHitSnapshot && (
            <div
              className='ih-subsurface ih-col ih-gap-1 ih-mt-3'
              style={{ padding: '9px 10px' }}
            >
              <div className='ih-row ih-between ih-gap-2'>
                <span className='ih-kicker'>Attached screening source</span>
                <span className='ih-badge ih-badge-info'>
                  {linkedHitSnapshot.agency}
                </span>
              </div>
              <div className='ih-font-medium ih-text-xs ih-truncate'>
                {linkedHitSnapshot.listName}
              </div>
              {(linkedHitSnapshot.program || linkedHitSnapshot.matchedHts) && (
                <div className='ih-text-xs ih-text-muted ih-truncate'>
                  {[
                    linkedHitSnapshot.program,
                    linkedHitSnapshot.matchedHts
                      ? `Matched HTS ${linkedHitSnapshot.matchedHts}`
                      : undefined,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              )}
            </div>
          )}

          <div className='ih-input-group ih-mt-3'>
            <span className='ih-input-group-icon'>
              <Search size={14} />
            </span>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder='Search fields…'
              className='ih-input ih-input-sm ih-input-search'
            />
          </div>
        </header>

        <div className='ih-pga-drawer-content'>
          <nav className='ih-pga-nav'>
            {schema.subGroups.map((sg) => {
              const reqInGroup = sg.fields.filter((f) => f.required).length;
              return (
                <button
                  key={sg.key}
                  type='button'
                  style={{ flexShrink: 0 }} // Prevents nav items from squishing vertically
                  onClick={() => {
                    setCollapsed((c) => ({ ...c, [sg.key]: false }));
                    scrollRef.current
                      ?.querySelector(`#pga-sg-${sg.key}`)
                      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className='ih-pga-nav-item'
                >
                  <span className='ih-truncate'>{sg.label}</span>
                  {reqInGroup > 0 && (
                    <span
                      className='ih-tone-warning'
                      style={{ fontSize: 9, flexShrink: 0 }}
                    >
                      ●
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          <div ref={scrollRef} className='ih-pga-fields'>
            <DocumentsSection
              documents={flag.documents ?? []}
              onAdd={
                onAddDocument ? () => docInputRef.current?.click() : undefined
              }
              onRemove={onRemoveDocument}
            />
            {schema.subGroups.map((sg) => {
              const visibleFields = sg.fields.filter(matchesSearch);
              if (needle && visibleFields.length === 0) return null;
              const isCollapsed = collapsed[sg.key];
              return (
                <section
                  key={sg.key}
                  id={`pga-sg-${sg.key}`}
                  className='ih-pga-group'
                  style={{ flexShrink: 0 }}
                >
                  <button
                    type='button'
                    onClick={() =>
                      setCollapsed((c) => ({ ...c, [sg.key]: !c[sg.key] }))
                    }
                    className='ih-pga-group-toggle'
                  >
                    <div className='ih-row ih-gap-1'>
                      {isCollapsed ? (
                        <ChevronRight size={14} className='ih-text-muted' />
                      ) : (
                        <ChevronDown size={14} className='ih-text-muted' />
                      )}
                      <span
                        className='ih-kicker'
                        style={{ fontSize: 12, fontWeight: 600 }}
                      >
                        {sg.label}
                      </span>
                      {sg.repeatable && (
                        <span className='ih-text-xs ih-text-muted'>
                          ({repeaters[sg.key]?.length ?? 0}
                          {sg.maxInstances ? `/${sg.maxInstances}` : ''})
                        </span>
                      )}
                    </div>
                  </button>
                  {!isCollapsed && (
                    <div className='ih-pga-group-body'>
                      {sg.repeatable ? (
                        <RepeaterBody
                          sg={sg}
                          instances={repeaters[sg.key] ?? []}
                          visibleFields={visibleFields}
                          onChange={(idx, field, v) =>
                            setRepeaterField(sg.key, idx, field, v)
                          }
                          onRemove={(idx) => removeRepeater(sg, idx)}
                          onAdd={() => addRepeater(sg)}
                        />
                      ) : (
                        <div className='ih-form-grid-2'>
                          {visibleFields.map((f) => (
                            <FieldInput
                              key={f.key}
                              field={f}
                              value={
                                values[f.storageKey ?? `${sg.key}.${f.key}`]
                              }
                              onChange={(v) => setField(sg.key, f, v)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        </div>

        {confirmRemove && (
          <div className='ih-pga-confirm import-hub' style={{ zIndex: 60 }}>
            <div className='ih-pga-confirm-card'>
              <h4 className='ih-font-semibold ih-text-sm'>
                {isDraft
                  ? 'Discard unsaved flag?'
                  : `Remove ${schema.agency} flag?`}
              </h4>
              <p className='ih-text-xs ih-text-muted ih-mt-2'>
                {isDraft
                  ? 'This draft has not been saved to the product. Discarding it will remove the imported and prefilled values.'
                  : 'This will detach the flag from the product and discard the datapoints you have entered. You can re-apply the flag anytime from a screening hit.'}
              </p>
              <div
                className='ih-row ih-gap-2 ih-mt-3'
                style={{ justifyContent: 'flex-end' }}
              >
                <button
                  type='button'
                  className='ih-btn ih-btn-outline ih-btn-sm'
                  onClick={() => setConfirmRemove(false)}
                >
                  Cancel
                </button>
                <button
                  type='button'
                  className='ih-btn ih-btn-danger ih-btn-sm'
                  disabled={readOnly}
                  title={readOnly ? 'Requires a Business license' : undefined}
                  onClick={() => {
                    onRemove();
                    setConfirmRemove(false);
                  }}
                >
                  {isDraft ? 'Discard draft' : 'Remove flag'}
                </button>
              </div>
            </div>
          </div>
        )}

        {reviewParsed && (
          <div className='ih-pga-confirm import-hub' style={{ zIndex: 60 }}>
            <div className='ih-pga-confirm-card' style={{ maxWidth: 560 }}>
              <div className='ih-row ih-between ih-gap-2'>
                <h4 className='ih-font-semibold ih-text-sm'>
                  Review parsed template
                </h4>
                <span className='ih-text-xs ih-text-muted ih-truncate'>
                  {reviewFileName}
                </span>
              </div>
              <p className='ih-text-xs ih-text-muted ih-mt-2'>
                {reviewParsed.matchedColumns} field
                {reviewParsed.matchedColumns === 1 ? '' : 's'} will populate the
                form. You can still edit any value after applying.
              </p>
              {reviewParsed.errors.length > 0 && (
                <ul
                  className='ih-col ih-gap-1 ih-text-xs ih-tone-danger ih-mt-2'
                  style={{ paddingLeft: 16, listStyle: 'disc' }}
                >
                  {reviewParsed.errors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              )}
              {reviewParsed.missingRequired.length > 0 && (
                <div className='ih-text-xs ih-tone-warning ih-mt-2'>
                  Missing required: {reviewParsed.missingRequired.join(', ')}
                </div>
              )}
              {reviewParsed.unmatchedHeaders.length > 0 && (
                <div className='ih-text-xs ih-text-muted ih-mt-2'>
                  Ignored headers: {reviewParsed.unmatchedHeaders.join(', ')}
                </div>
              )}
              {reviewParsed.duplicateHeaders.length > 0 && (
                <div className='ih-text-xs ih-tone-warning ih-mt-2'>
                  Duplicate headers: {reviewParsed.duplicateHeaders.join(', ')}
                </div>
              )}
              <div
                className='ih-pga-review-grid ih-mt-3'
                style={{ maxHeight: 240, overflowY: 'auto' }}
              >
                {Object.entries(reviewParsed.values)
                  .slice(0, 30)
                  .map(([k, v]) => (
                    <div key={k} className='ih-pga-review-row'>
                      <span className='ih-text-xs ih-text-muted ih-truncate'>
                        {k}
                      </span>
                      <span className='ih-text-xs ih-font-medium ih-truncate'>
                        {typeof v === 'object' && v !== null
                          ? `${(v as { amount?: string }).amount ?? ''} ${
                              (v as { uom?: string }).uom ?? ''
                            }`.trim() || '—'
                          : String(v)}
                      </span>
                    </div>
                  ))}
              </div>
              <div
                className='ih-row ih-gap-2 ih-mt-3'
                style={{ justifyContent: 'flex-end' }}
              >
                <button
                  type='button'
                  className='ih-btn ih-btn-outline ih-btn-sm'
                  onClick={() => {
                    setReviewParsed(null);
                    setReviewFileName(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type='button'
                  className='ih-btn ih-btn-primary ih-btn-sm'
                  onClick={applyParsedValues}
                  disabled={reviewParsed.matchedColumns === 0 || readOnly}
                  title={readOnly ? 'Requires a Business license' : undefined}
                >
                  <CheckCircle2 size={14} /> Apply to form
                </button>
              </div>
            </div>
          </div>
        )}
      </aside>
    </div>,
    document.body,
  );
};

const RepeaterBody: React.FC<{
  sg: PgaSubGroup;
  instances: Record<string, unknown>[];
  visibleFields: PgaField[];
  onChange: (idx: number, field: PgaField, v: unknown) => void;
  onRemove: (idx: number) => void;
  onAdd: () => void;
}> = ({ sg, instances, visibleFields, onChange, onRemove, onAdd }) => {
  const readOnly = useImportHubReadOnly();
  const atMax = sg.maxInstances != null && instances.length >= sg.maxInstances;
  return (
    <div className='ih-col ih-gap-2'>
      {instances.length === 0 && (
        <p className='ih-text-xs ih-text-muted ih-italic'>
          No {sg.itemLabel ?? 'entries'} yet.
        </p>
      )}
      {instances.map((inst, idx) => (
        <div key={idx} className='ih-pga-repeater-item'>
          <div className='ih-row ih-between'>
            <div className='ih-kicker ih-font-medium'>
              {sg.itemLabel ?? 'Entry'} #{idx + 1}
            </div>
            <button
              type='button'
              onClick={() => onRemove(idx)}
              disabled={readOnly}
              title={readOnly ? 'Requires a Business license' : undefined}
              className='ih-pga-repeater-remove'
            >
              Remove
            </button>
          </div>
          <div className='ih-form-grid-2 ih-mt-2'>
            {visibleFields.map((f) => (
              <FieldInput
                key={f.key}
                field={f}
                value={inst[f.key]}
                onChange={(v) => onChange(idx, f, v)}
              />
            ))}
          </div>
        </div>
      ))}
      <button
        type='button'
        className='ih-btn ih-btn-outline ih-btn-sm'
        onClick={onAdd}
        disabled={atMax || readOnly}
        title={readOnly ? 'Requires a Business license' : undefined}
      >
        {readOnly ? <Lock size={14} /> : <Plus size={14} />} Add{' '}
        {sg.itemLabel ?? 'entry'}
        {sg.maxInstances != null && (
          <span className='ih-text-muted'>
            ({instances.length}/{sg.maxInstances})
          </span>
        )}
      </button>
    </div>
  );
};

const FieldInput: React.FC<{
  field: PgaField;
  value: unknown;
  onChange: (v: unknown) => void;
}> = ({ field, value, onChange }) => {
  const labelEl = (
    <div className='ih-pga-field-label'>
      <label className='ih-kicker ih-font-medium'>{field.label}</label>
      {field.required && <span className='ih-tone-warning'>*</span>}
    </div>
  );
  switch (field.inputType) {
    case 'select':
      const selectedValue = String(value ?? '');
      const hasUploadedOption =
        !!selectedValue && !field.options?.includes(selectedValue);
      return (
        <div>
          {labelEl}
          <select
            className='ih-select ih-input-sm'
            value={selectedValue}
            onChange={(e) => onChange(e.target.value)}
          >
            <option value=''>— select —</option>
            {hasUploadedOption && (
              <option value={selectedValue}>{selectedValue}</option>
            )}
            {field.options?.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>
      );
    case 'yesno':
      return (
        <div>
          {labelEl}
          <div className='ih-row ih-gap-2'>
            {['Yes', 'No'].map((o) => (
              <button
                key={o}
                type='button'
                onClick={() => onChange(o)}
                className={cx(
                  'ih-btn',
                  value === o ? 'ih-btn-primary' : 'ih-btn-outline',
                )}
              >
                {o}
              </button>
            ))}
          </div>
        </div>
      );
    case 'date':
      return (
        <div>
          {labelEl}
          <input
            type='date'
            className='ih-input ih-input-sm'
            value={String(value ?? '')}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      );
    case 'number':
      return (
        <div>
          {labelEl}
          <input
            type='number'
            className='ih-input ih-input-sm'
            value={String(value ?? '')}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      );
    case 'quantity': {
      const v = (value as { amount?: string; uom?: string } | undefined) ?? {};
      return (
        <div>
          {labelEl}
          <div className='ih-row ih-gap-1'>
            <input
              type='number'
              placeholder='Amount'
              className='ih-input ih-input-sm ih-grow'
              value={String(v.amount ?? '')}
              onChange={(e) => onChange({ ...v, amount: e.target.value })}
            />
            <select
              className='ih-select ih-input-sm'
              style={{ width: 80 }}
              value={String(v.uom ?? '')}
              onChange={(e) => onChange({ ...v, uom: e.target.value })}
            >
              <option value=''>UOM</option>
              {field.uomOptions?.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
        </div>
      );
    }
    default:
      return (
        <div>
          {labelEl}
          <input
            type='text'
            className='ih-input ih-input-sm'
            placeholder={field.placeholder}
            value={String(value ?? '')}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      );
  }
};

const DocumentsSection: React.FC<{
  documents: PgaFlagDocument[];
  onAdd?: () => void;
  onRemove?: (id: string) => void;
}> = ({ documents, onAdd, onRemove }) => {
  const readOnly = useImportHubReadOnly();
  return (
    <section className='ih-pga-group' style={{ flexShrink: 0 }}>
      <div
        className='ih-row ih-between ih-gap-2'
        style={{ padding: '8px 12px' }}
      >
        <div className='ih-row ih-gap-1'>
          <FileText size={13} className='ih-tone-info' />
          <span className='ih-kicker' style={{ fontSize: 12, fontWeight: 600 }}>
            Documents
          </span>
          <span className='ih-text-xs ih-text-muted ih-num'>
            ({documents.length})
          </span>
        </div>
        {onAdd && (
          <button
            type='button'
            onClick={readOnly ? undefined : onAdd}
            disabled={readOnly}
            title={readOnly ? 'Requires a Business license' : undefined}
            className='ih-btn ih-btn-outline ih-btn-sm'
          >
            {readOnly ? <Lock size={12} /> : <Plus size={12} />} Attach
          </button>
        )}
      </div>
      <div className='ih-pga-group-body'>
        {documents.length === 0 ? (
          <p className='ih-text-xs ih-text-muted ih-italic'>
            No documents attached to this flag yet. Attach the PGA filing,
            certificate, or lab report that backs it.
          </p>
        ) : (
          <div className='ih-pga-doc-list'>
            {documents.map((d) => (
              <div key={d.id} className='ih-pga-doc-row'>
                <FileText size={14} className='ih-tone-info' />
                <div className='ih-pga-doc-row-name'>
                  <div className='ih-text-xs ih-font-medium ih-truncate'>
                    {d.name}
                  </div>
                  <div className='ih-pga-doc-row-meta ih-truncate'>
                    {formatKind(d.kind)} · {formatBytes(d.size)} ·{' '}
                    {new Date(d.uploadedAt).toLocaleDateString()}
                  </div>
                </div>
                {onRemove && (
                  <button
                    type='button'
                    onClick={() => (readOnly ? undefined : onRemove(d.id))}
                    disabled={readOnly}
                    className='ih-icon-btn'
                    aria-label={`Remove ${d.name}`}
                    title={readOnly ? 'Requires a Business license' : 'Remove'}
                  >
                    {readOnly ? <Lock size={13} /> : <Trash2 size={13} />}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function formatKind(mime: string): string {
  if (!mime) return 'file';
  if (mime.includes('pdf')) return 'PDF';
  if (mime.includes('csv')) return 'CSV';
  if (mime.includes('sheet') || mime.includes('excel')) return 'XLSX';
  if (mime.startsWith('image/')) return mime.split('/')[1].toUpperCase();
  const parts = mime.split('/');
  return (parts[1] ?? parts[0]).toUpperCase();
}
